/**
 * รายละเอียดข้อมูลสินค้า — `GET /v1/products/{product_id}`
 *
 * สองเรื่องที่ห้ามพลาดในหน้านี้:
 *
 * 1. `plant_data` ถูกกรองด้วย plant scope ของผู้เรียก (D-004) หน้าจอจึงต้องพูดว่า
 *    "โรงงานที่คุณมีสิทธิ์เห็น" ไม่ใช่ "ทุกโรงงานที่มีสินค้านี้" การพูดผิดข้อนี้
 *    สอนให้พนักงานเชื่อว่าสินค้าหายไปจากโรงงานหนึ่ง ทั้งที่เขาแค่มองไม่เห็น
 * 2. ฟิลด์ผู้กระทำทั้งห้าเป็น read-only ที่เซิร์ฟเวอร์เติมเอง — หน้านี้แสดงว่า
 *    ใครสร้างและใครอนุมัติ ซึ่งเป็นครึ่งที่มองเห็นได้ของ D-010 ไม่มีช่องให้แก้
 *
 * ตัวเลข `NUMERIC` แสดงเป็น string ที่ได้รับมาตรง ๆ ไม่ผ่าน `Number` — ค่าที่
 * double ปัดทิ้งไปแล้วจะไม่มีวันกลับมา และต้นทุนที่เพี้ยนคือข้อมูลบัญชีที่ผิด
 *
 * ลิงก์ "แก้ไขข้อมูล" โผล่เฉพาะตอนที่รายการยังเป็นร่างและสิทธิ์มี `can_edit` จริง
 * และพาไปที่หน้าจอแก้ไข (`ProductEditView`) ซึ่งเป็นที่เดียวที่เรียก `PATCH`
 *
 * คำสั่ง submit/approve/reject อยู่ใน `ProductTransitionPanel` และเปลี่ยนสิ่งที่
 * หน้านี้แสดงได้ทางเดียว: ส่ง record ที่เซิร์ฟเวอร์ยืนยันแล้วกลับมาทาง
 * `onProductChanged` — ไม่มีการเดาผลล่วงหน้า และ record ที่ได้มาไม่ถูกเก็บลง
 * storage ใด ๆ (D-049 อนุญาตเฉพาะ session token) เพราะสำเนาที่ค้างไว้คือภาพเก่า
 * ของสิ่งที่คนอื่นอาจเพิ่งอนุมัติไป
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductFailureNotice } from './ProductFailureNotice';
import { ProductImagesPanel } from './ProductImagesPanel';
import { ProductTransitionPanel } from './ProductTransitionPanel';
import {
  changeRequestsSearch,
  editSearch,
  productStatusLabel,
  productStatusTone,
  orDash,
  steelSummary,
} from './productView';
import { StatusBadge } from '../StatusBadge';
import { bomRoutingSearch } from '../bom/bomView';
import { InfoHint } from '../components/InfoHint';
import { BOM_ROUTING_PATH } from '../routes';
import { fetchProduct, type ProductFailure } from '../../lib/products/client';
import { BOM_ROUTING_MODULE } from '../../lib/bom/types';
import {
  PRODUCT_AUTHORSHIP_FIELDS,
  PRODUCT_EDITABLE_STATUS,
  PRODUCT_MASTER_MODULE,
  type Product,
  type ProductAuthorshipField,
} from '../../lib/products/types';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface ProductDetailViewProps {
  readonly productId: string;
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type DetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly product: Product }
  | ProductFailure;

/**
 * ป้ายกำกับของทั้งห้าฟิลด์ — ผูกชนิดไว้กับรายการจริงใน contract เพื่อให้ฟิลด์ที่
 * เพิ่มเข้ามาแล้วลืมใส่ป้าย กลายเป็น compile error ไม่ใช่ช่องว่างบนหน้าจอ
 */
const AUTHORSHIP_LABELS: Readonly<Record<ProductAuthorshipField, string>> = {
  created_by_employee_id: 'ผู้สร้าง',
  submitted_by_employee_id: 'ผู้ส่งอนุมัติ',
  submitted_at: 'เวลาที่ส่งอนุมัติ',
  approved_by_employee_id: 'ผู้อนุมัติ / ผู้ปฏิเสธ',
  approved_at: 'เวลาที่ตัดสิน',
};

export function ProductDetailView({
  productId,
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ProductDetailViewProps) {
  const { pathname } = useLocation();
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<DetailState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchProduct(productId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [productId, apiBaseUrl, fetchImpl, reloadToken]);

  const product = state.status === 'ready' ? state.product : null;

  /**
   * เสนอ "แก้ไข" ก็ต่อเมื่อทั้งสองข้อจริงพร้อมกัน: รายการยังเป็นร่าง (contract
   * ให้ `PATCH` เฉพาะ `DRAFT`) และสิทธิ์ที่เซิร์ฟเวอร์ส่งมามี `can_edit` จริง
   * อ่าน flag จาก `modules` ผ่าน `can()` เท่านั้น ไม่ derive กติกา ADMIN/AUDITOR
   * ซ้ำจาก `system_roles`
   *
   * ⚠️ การซ่อนลิงก์เป็นความสะดวก ไม่ใช่ด่าน — เซิร์ฟเวอร์ตรวจทั้งสิทธิ์และสถานะเอง
   * ทุกคำขอ และ URL ของหน้าแก้ไขยังเปิดตรงได้อยู่ ซึ่งหน้านั้นก็อธิบายเองว่าทำไมแก้ไม่ได้
   */
  const offersEdit =
    product !== null &&
    product.status === PRODUCT_EDITABLE_STATUS &&
    can(permissions, PRODUCT_MASTER_MODULE, 'EDIT');

  /**
   * ทางไปคำขอเปลี่ยนแปลง — เสนอเมื่อรายการ `ACTIVE` แล้ว ซึ่งเป็นสถานะเดียวที่
   * contract บอกว่าเสนอแก้ได้ ("ใช้เมื่อ record เป็น `ACTIVE` แล้ว")
   *
   * ไม่ผูกกับ `can_edit` เพราะหน้านั้นเปิดด้วยสิทธิ์ VIEW ได้จริง — คนที่ดูอย่างเดียว
   * ก็ต้องเห็นว่ามีคำขอค้างอยู่ ส่วนฟอร์มเสนอคำขอถูก gate ด้วย `can_edit` ที่หน้านั้นเอง
   */
  const offersChangeRequests = product !== null && product.status === 'ACTIVE';

  /**
   * ทางไป BOM/Routing ของสินค้าตัวนี้ — เป็นคนละโมดูลใน RBAC matrix (`BOM_ROUTING`)
   * จึงถาม flag ของโมดูลนั้นตรง ๆ ไม่ใช่อาศัยสิทธิ์ที่พาผู้ใช้มาถึงหน้านี้
   *
   * เสนอกับทุกสถานะของสินค้า ไม่ใช่เฉพาะ `ACTIVE`: การ **ดู** สูตรที่มีอยู่ต้องทำได้
   * เสมอ ส่วนการ **เขียน** ฉบับใหม่ต้องให้สินค้าเป็น `ACTIVE` ก่อน ซึ่งเซิร์ฟเวอร์
   * ตอบด้วยกฎ `PRODUCT_NOT_ACTIVE` และหน้าจอนั้นอธิบายเองเมื่อถึงตอนนั้น
   *
   * ⚠️ การซ่อนลิงก์เป็นความสะดวก ไม่ใช่ด่าน — route ปลายทางผ่าน `can()` เดียวกัน
   * และ backend เป็นผู้ปฏิเสธ request จริงเสมอ
   */
  const offersBomRouting =
    product !== null && can(permissions, BOM_ROUTING_MODULE, 'VIEW');

  /**
   * ป้ายความโปร่งใส (D-140/D-141) — ผู้สร้างอนุมัติสินค้าตัวเองได้แล้ว หน้านี้จึง
   * ต้องบอกตรง ๆ เมื่อเกิดขึ้นจริง ไม่ใช่ปล่อยให้ดูเหมือนมีคนที่สองตรวจ
   */
  const selfApproved =
    product !== null &&
    product.approved_by_employee_id !== null &&
    product.approved_by_employee_id === product.created_by_employee_id;

  return (
    <div className="cap-page" data-testid="product-detail">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="product-detail-code">
            {product === null ? 'รายละเอียดข้อมูลสินค้า' : product.product_code}
          </h1>
          <p className="cap-page__sub">
            {product === null ? `รหัสภายใน ${productId}` : product.name}
          </p>
        </div>
        <div className="cap-page__actions">
          {product === null ? null : (
            <StatusBadge tone={productStatusTone(product.status)}>
              {productStatusLabel(product.status)}
            </StatusBadge>
          )}
          {!offersEdit ? null : (
            <Link
              className="cap-button cap-button--primary"
              data-testid="product-edit-link"
              to={{ pathname, search: editSearch(productId) }}
            >
              แก้ไขข้อมูล
            </Link>
          )}
          {!offersChangeRequests ? null : (
            <Link
              className="cap-button cap-button--primary"
              data-testid="product-change-requests-link"
              to={{ pathname, search: changeRequestsSearch(productId) }}
            >
              คำขอเปลี่ยนแปลง
            </Link>
          )}
          {!offersBomRouting ? null : (
            <Link
              className="cap-button cap-button--ghost"
              data-testid="product-bom-routing-link"
              to={{ pathname: BOM_ROUTING_PATH, search: bomRoutingSearch(productId) }}
            >
              BOM และ Routing
            </Link>
          )}
          <Link className="cap-button cap-button--ghost" to={{ pathname, search: '' }}>
            กลับไปรายการสินค้า
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="product-detail-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายละเอียดสินค้า…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <ProductFailureNotice
          failure={state}
          context="detail"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {product === null ? null : (
        <>
          <ProductTransitionPanel
            product={product}
            permissions={permissions}
            apiBaseUrl={apiBaseUrl}
            fetchImpl={fetchImpl}
            onProductChanged={(next) => {
              setState({ status: 'ready', product: next });
            }}
          />

          <section className="cap-meta" aria-label="ข้อมูลทั่วไปของสินค้า">
            <div className="cap-meta__item">
              <div className="cap-meta__label">รุ่น/โมเดล</div>
              <div className="cap-meta__value" data-testid="product-model">
                {orDash(product.model)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ประเภทสินค้า</div>
              <div className="cap-meta__value" data-testid="product-type">
                {product.product_type}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">หน่วยนับหลัก</div>
              <div className="cap-meta__value" data-testid="product-base-uom">
                {product.base_uom}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">น้ำหนักต่อชิ้น (กก.)</div>
              <div className="cap-meta__value" data-testid="product-kg-per-piece">
                {orDash(product.kg_per_piece)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">เหล็ก (เกรด · รูปทรง · หนา × กว้าง × ยาว)</div>
              <div className="cap-meta__value" data-testid="product-steel">
                {steelSummary(product)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สถานะ (D-011)</div>
              <div className="cap-meta__value" data-testid="product-status">
                {productStatusLabel(product.status)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สร้างเมื่อ</div>
              <div className="cap-meta__value">{product.created_at}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">แก้ไขล่าสุด</div>
              <div className="cap-meta__value">{product.updated_at}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">รหัสภายใน (product_id)</div>
              <div className="cap-meta__value cap-table__code">{product.product_id}</div>
            </div>
          </section>

          <ProductImagesPanel
            key={product.product_id}
            productId={product.product_id}
            productStatus={product.status}
            permissions={permissions}
            apiBaseUrl={apiBaseUrl}
            fetchImpl={fetchImpl}
          />

          <section className="cap-card cap-card--table" aria-label="ผู้ดำเนินการตามเอกสาร">
            <div className="cap-card__head">
              <h2 className="cap-card__title">
                ผู้ดำเนินการ (อ่านอย่างเดียว)
                <InfoHint
                  label="เกี่ยวกับฟิลด์ผู้ดำเนินการ"
                  text="เซิร์ฟเวอร์เป็นผู้เติมจากผู้ที่ล็อกอินจริง — หน้าจอนี้ส่งค่าเหล่านี้ไม่ได้"
                />
              </h2>
            </div>
            <div className="cap-meta cap-meta--flush">
              {PRODUCT_AUTHORSHIP_FIELDS.map((field) => (
                <div className="cap-meta__item" key={field}>
                  <div className="cap-meta__label">{AUTHORSHIP_LABELS[field]}</div>
                  <div className="cap-meta__value" data-testid={`product-${field}`}>
                    {orDash(product[field])}
                  </div>
                  {field === 'approved_by_employee_id' && selfApproved ? (
                    <span data-testid="product-self-approved-badge">
                      <StatusBadge tone="warning">อนุมัติโดยผู้สร้างเอง</StatusBadge>
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          <section className="cap-card cap-card--table" aria-label="ข้อมูลระดับโรงงาน">
            <div className="cap-card__head">
              <h2 className="cap-card__title">
                ข้อมูลระดับโรงงาน
                <InfoHint
                  label="เกี่ยวกับขอบเขตโรงงานที่แสดงในตารางนี้"
                  text={
                    <span data-testid="product-plant-scope-note">
                      แสดงเฉพาะโรงงานที่คุณมีสิทธิ์เห็น (D-004) — ไม่ใช่ทุกโรงงานที่มีสินค้านี้อยู่
                    </span>
                  }
                />
              </h2>
            </div>
            {product.plant_data.length === 0 ? (
              <p className="cap-card__foot" data-testid="product-plant-empty">
                ไม่มีโรงงานที่คุณมีสิทธิ์เห็นสำหรับสินค้านี้
              </p>
            ) : (
              <div className="cap-table-wrap">
                <table className="cap-table">
                  <caption className="cap-visually-hidden">
                    ข้อมูลระดับโรงงานของสินค้านี้ เฉพาะโรงงานที่ผู้ใช้ปัจจุบันมีสิทธิ์เห็น
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">โรงงาน</th>
                      <th scope="col">โหมดเติมเต็ม</th>
                      <th scope="col">วิธีกำหนดขนาดล็อต</th>
                      <th scope="col">Lead time (วัน)</th>
                      <th scope="col">สต๊อกปลอดภัย</th>
                      <th scope="col">ต้นทุนต่อหน่วย</th>
                      <th scope="col">Valuation class</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.plant_data.map((plant) => (
                      <tr key={plant.plant_id} data-testid={`product-plant-${plant.plant_code}`}>
                        <th scope="row" className="cap-table__row-head">
                          {plant.plant_code}
                        </th>
                        <td className="cap-table__code">{plant.default_replenishment_mode}</td>
                        <td className="cap-table__muted">{orDash(plant.lot_size_procedure)}</td>
                        <td className="cap-table__code">{orDash(plant.planned_delivery_time)}</td>
                        <td className="cap-table__code" data-testid={`product-safety-stock-${plant.plant_code}`}>
                          {orDash(plant.safety_stock)}
                        </td>
                        <td className="cap-table__code" data-testid={`product-unit-cost-${plant.plant_code}`}>
                          {orDash(plant.unit_cost)}
                        </td>
                        <td className="cap-table__muted">{orDash(plant.valuation_class)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
